import React, { useEffect, useState, Suspense, lazy } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Switch } from 'react-router-dom';
import { IntlProvider } from "react-intl";

import { setLanguage, logout } from './store/actions';
import { messages } from './assets/data/vocabulary'

import './App.scss';

import SplashScreen from './components/UI/SplashScreen/SplashScreen';

import ProtectedRoute from './services/ProtectedRoute';
import Layout from './hoc/Layout';

import GetRestorableAccount from './components/AuthenticationForm/PasswordRecovery/GetRestorableAccount/GetRestorableAccount'
import CreatePasswordViaCode from './components/AuthenticationForm/PasswordRecovery/NewPassword/CreatePasswordViaCode/CreatePasswordViaCode'
import PasswordSuccessRecovery from './components/AuthenticationForm/PasswordRecovery/NewPassword/PasswordSuccessRecovery/PasswordSuccessRecovery'
import VerifyContactsNotification from './components/PersonalAccount/ProfileSettings/VerifyContactsNotification/VerifyContactsNotification'
import RestorableAccountBlocked from './components/AuthenticationForm/PasswordRecovery/RestorableAccountBlocked/RestorableAccountBlocked'
import ChooseRecoveryMethod from './components/AuthenticationForm/PasswordRecovery/ChooseRecoveryMethod/ChooseRecoveryMethod'
import MaskRecovery from './components/AuthenticationForm/PasswordRecovery/MaskRecovery/MaskRecovery'
import CreatePasswordViaHash from './components/AuthenticationForm/PasswordRecovery/NewPassword/CreatePasswordViaHash/CreatePasswordViaHash'
import Calendar from "./components/MonthCalendar/Calendar";

const HomePage = lazy(() => import('./components/HomePage/HomePage'));
const Phonebook = lazy(() => import('./components/Phonebook/Phonebook'));
const Documentation = lazy(() => import('./components/Documentation/Documentation'));
const DepartmentsTree = lazy(() => import('./components/DepartmentsTree/DepartmentsTree'));
const EmployeesTable = lazy(() => import('./components/Employees/EmployeesTable/EmployeesTable'));
const About = lazy(() => import('./components/About/About'));
const Disciplines = lazy(() => import('./components/Disciplines/Disciplines'));
const Rating = lazy(() => import('./components/Rating/Rating'));
const NotFound = lazy(() => import('./components/UI/NotFound/NotFound'));
const EmployeePersonCard = lazy(() => import('./components/Employees/EmployeePersonCard/EmployeePersonCard'));
const DepartmentInfo = lazy(() => import('./components/DepartmentInfo/DepartmentInfo'));
const AuthenticationForm = lazy(() => import('./components/AuthenticationForm/AuthenticationForm'));
const ChangePasswordAuth = lazy(() => import('./components/AuthenticationForm/ChangePasswordAuth/ChangePasswordAuth'));
const Study = lazy(() => import('./components/PersonalAccount/Study/Study'));
const Test = lazy(() => import('./components/PersonalAccount/Test/Test'));
const Markbook = lazy(() => import('./components/PersonalAccount/Markbook/Markbook'));
const PersonalRating = lazy(() => import('./components/PersonalAccount/PersonalRating/PersonalRating'));
const Profile = lazy(() => import('./components/PersonalAccount/Profile/Profile'));
const GroupInfo = lazy(() => import('./components/PersonalAccount/GroupInfo/GroupInfo'));
const Library = lazy(() => import('./components/PersonalAccount/Library/Library'));
const DormitoryPage = lazy(() => import('./components/PersonalAccount/Dormitory/DormitoryPage'));
const Announcement = lazy(() => import('./components/PersonalAccount/Announcement/Announcement'));
const Graduate = lazy(() => import('./components/PersonalAccount/Graduate/Graduate'));
const StudentsList = lazy(() => import('./components/StudentsList/StudentsList'));
const ProfileSettings = lazy(() => import('./components/PersonalAccount/ProfileSettings/ProfileSettings'));
const ContactVerification = lazy(() => import('./components/PersonalAccount/ProfileSettings/ContactVerification/ContactVerification'));
const AdminAuth = lazy(() => import('./components/AuthenticationForm/AdminAuth/AdminAuth'));
const BugReportScreen = lazy(() => import('./components/BugReport/BugReportScreen/BugReportScreen'));
const Headman = lazy(() => import('./components/PersonalAccount/Headman/Headman'));
const PremiumPenaltyPage = lazy(() => import('./components/PersonalAccount/Penalty/PremiumPenaltyPage'));
const SocialAndResearchWorkPage = lazy(() => import('./components/PersonalAccount/SocialAndResearchWork/SocialAndResearchWorkPage'));
const CommonSchedule = lazy(() => import('./components/Schedule/CommonSchedule/CommonSchedule'));
const OmissionPage = lazy(() => import('./components/PersonalAccount/Omissions/OmissionPage'))
const ProfilingPage = lazy(() => import('./components/PersonalAccount/Profiling/ProfilingPage'))

// const GetRestorableAccount = lazy(() => import('./components/AuthenticationForm/PasswordRecovery/GetRestorableAccount/GetRestorableAccount'))
// const CreatePasswordViaCode = lazy(() => import('./components/AuthenticationForm/PasswordRecovery/NewPassword/CreatePasswordViaCode/CreatePasswordViaCode'))
// const PasswordSuccessRecovery = lazy(() => import('./components/AuthenticationForm/PasswordRecovery/NewPassword/PasswordSuccessRecovery/PasswordSuccessRecovery'))
// const VerifyContactsNotification = lazy(() => import('./components/PersonalAccount/ProfileSettings/VerifyContactsNotification/VerifyContactsNotification'))
// const RestorableAccountBlocked = lazy(() => import('./components/AuthenticationForm/PasswordRecovery/RestorableAccountBlocked/RestorableAccountBlocked'))
// const ChooseRecoveryMethod = lazy(() => import('./components/AuthenticationForm/PasswordRecovery/ChooseRecoveryMethod/ChooseRecoveryMethod'))
// const MaskRecovery = lazy(() => import('./components/AuthenticationForm/PasswordRecovery/MaskRecovery/MaskRecovery'))
// const CreatePasswordViaHash = lazy(() => import('./components/AuthenticationForm/PasswordRecovery/NewPassword/CreatePasswordViaHash/CreatePasswordViaHash'))

function App() {
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)
  const user = useSelector(state => state.auth.user)
  const theme = useSelector(state => state.general.theme)

  const language = useSelector(state => state.general.language)
  const setLanguageHandler = (lang) => {
    localStorage.setItem('lang', JSON.stringify(lang))
    dispatch(setLanguage(lang))
  }

  useEffect(() => {
    let tmp = 'light'
    localStorage.setItem("theme", JSON.stringify(tmp))
    if (localStorage.getItem("theme")) {
      const currentTheme = JSON.parse(localStorage.getItem("theme"))
      document.documentElement.className = ""
      if (currentTheme !== 'light') document.documentElement.classList.add(`theme--dark`)
      else document.documentElement.classList.add(`theme--default`)
    } else {
      localStorage.setItem("theme", JSON.stringify('light'))
    }

    if (localStorage.getItem("_expiredTime")) {
      const expiredTime = parseInt(localStorage.getItem("_expiredTime"), 10);
      if (expiredTime < Date.now()) {
        setLoading(true)
      }
    }
  }, [])

  useEffect(() => {
    if (loading)
      dispatch(logout()).then(() => setLoading(false))
  }, [loading, dispatch])

  return (
    <div className={`App ${'theme ' + (theme !== 'dark' ? 'theme--default' : 'theme--dark')}`}>
      <IntlProvider messages={messages[language.code]} locale={language.code} defaultLocale="ru">
        {
          loading ? <SplashScreen /> :
            <Layout setLanguage={setLanguageHandler}>
              <Suspense fallback={<div className="Layout_content_color" style={{ width: 0, height: 0 }} />}>
                <Switch >
                  <Route path="/about" exact component={About} />
                  <Route path="/api" exact component={Documentation} />
                  <Route path="/support" exact component={BugReportScreen} />
                  <Route path="/departments/:id?" exact component={DepartmentsTree} />
                  <Route path="/schedule-department" exact component={DepartmentInfo} />
                  <Route path="/employees/:id" exact component={EmployeePersonCard} />
                  <Route path="/contact-confirm" exact component={ContactVerification} />
                  <Route path="/list-of-disciplines" exact component={Disciplines} />
                  <Route path="/login" exact component={AuthenticationForm} />
                  <Route path="/login/password-change" exact component={ChangePasswordAuth} />
                  <Route path="/new-password" exact component={CreatePasswordViaCode} />
                  <Route path="/initial-password" exact component={CreatePasswordViaHash} />
                  <Route path="/phones" exact component={Phonebook} />
                  <Route path="/profiles" exact component={StudentsList} />
                  <Route path="/rating-of-students" exact component={Rating} />
                  <Route path="/password-recovery" exact component={GetRestorableAccount} />
                  <Route path="/password-recovery/error" exact component={RestorableAccountBlocked} />
                  <Route path="/password-recovery/contacts" exact component={ChooseRecoveryMethod} />
                  <Route path="/password-recovery/mask" exact component={MaskRecovery} />
                  <Route path="/password-recovery/success" exact component={PasswordSuccessRecovery} />
                  <Route path="/schedule/:id?" component={CommonSchedule} />
                  <Route path="/calendar" component={Calendar} />
                  <Route path="/" exact component={HomePage} />
                  <Route path="/not-found" exact component={NotFound} />

                  <ProtectedRoute path="/admin" exact component={AdminAuth} />
                  <ProtectedRoute path="/personal-account/verify-contacts" exact component={VerifyContactsNotification} />
                  <ProtectedRoute path="/personal-account/profile" exact component={Profile} />
                  <ProtectedRoute path="/personal-account/markbook" exact component={Markbook} />
                  <ProtectedRoute path="/personal-account/study" exact component={Study} />
                  <ProtectedRoute path="/personal-account/settings" exact component={ProfileSettings} />
                  <ProtectedRoute path="/personal-account/notifications" exact component={Disciplines} />
                  <ProtectedRoute path="/personal-account/rating" exact component={PersonalRating} />
                  <ProtectedRoute path="/personal-account/group" exact component={GroupInfo} />
                  <ProtectedRoute path="/personal-account/library" exact component={Library} />
                  <ProtectedRoute path="/personal-account/test" exact component={Test} />
                  <ProtectedRoute path="/personal-account/dormitory" exact component={DormitoryPage} />
                  <ProtectedRoute path="/personal-account/announcements" exact component={Announcement} />
                  <ProtectedRoute path="/personal-account/graduate" exact component={Graduate} />
                  <ProtectedRoute path="/personal-account/penalty" exact component={PremiumPenaltyPage} />
                  <ProtectedRoute path="/personal-account/omissions" exact component={OmissionPage}/>
                  <ProtectedRoute path="/personal-account/social-and-research-work" exact component={SocialAndResearchWorkPage}/>
                  <ProtectedRoute path="/personal-account/profiling" exact component={ProfilingPage}/>
                  {/* <ProtectedRoute path="/personal-account/omissions" exact component={StudentOmissionCertificates}/> */}
                  {(user?.isGroupHead || user?.canStudentNote) && <ProtectedRoute path="/personal-account/headman" exact component={Headman} />}

                  <Route component={NotFound} />
                </Switch >
              </Suspense>
            </Layout>
        }
      </IntlProvider>
    </div>
  );
}

export default App;
